import SparkMD5 from 'spark-md5';

import type { HtmlEditApplierDeps, HtmlEditApplierMode, ParagraphStateReader } from './shared';
import { documentIdSchema, getBlockCardAnchor, getIsCancellationError } from './shared';
import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import { serializeRemovedTextLinesToPreviewHtml } from '../../serializers/html/removed-content-preview';
import type { AlineEditor } from '../../slate-api';
import {
  AlineTransforms,
  applyRemoveColumnsOutput,
  applyRemoveOutput,
  applyRemoveRowsOutput,
  generateSuggestedEditGroupId,
  parseAnchorId,
  partitionRemoveAnchorsIntoRuns,
  partitionRemoveColumnAnchorsIntoRuns,
  partitionRemoveRowAnchorsIntoRuns,
} from '../../slate-api';
import type {
  HtmlEdit,
  RemoveColumnsSuggestion,
  RemoveRowsSuggestion,
  RemoveSuggestion,
} from '../redline-llm-output';
import type {
  CodeBlockRedlineEditError,
  CodeBlockRedlineItem,
  CodeBlockRedlineRemove,
} from '../redline-shapes';

/** The removed content one applied run contributes to its card preview. */
type RemovedRunContent = { removedHtml: string };

/**
 * One validated anchor: what the card should point at (the element's own id —
 * bare coordinates on PDF), the ready-to-render HTML it contributes to a
 * PDF preview card, and the text the removal depends on — null when the
 * anchor has no text dependency (table rows).
 */
type ValidatedAnchor = {
  cardAnchor: string;
  dependencyText: string | null;
  previewHtml: string;
};

type AnchorValidation = { ok: true; value: ValidatedAnchor } | { message: string; ok: false };

/**
 * The parts that differ between the remove / remove-rows / remove-columns
 * pipelines. Everything else — dedupe, atomic anchor validation, document-
 * order sorting, the read-only-PDF short circuit, per-run apply with partial
 * failure handling — is shared in `applyRemoveSuggestionWithPipeline`.
 */
type RemovePipeline<TRun> = {
  /**
   * Apply one partitioned run to the editor as its own suggestion under
   * `groupId`, returning the removed content for the card preview.
   */
  applyRun: (editor: AlineEditor, run: TRun, groupId: string) => RemovedRunContent;
  nouns: {
    /** The apply transform's name, used in the thrown-error message. */
    applyName: string;
    /** The suggestion type, used in the empty-ids error message. */
    suggestionName: HtmlEdit['suggest']['_type'];
  };
  /** Partition the document-ordered ids into runs, each applied as one suggestion. */
  partition: (editor: AlineEditor, ids: string[]) => TRun[];
  /** The card anchors for one run: its first and last removed anchors, as listed. */
  runAnchors: (run: TRun) => { endLocationAnchor: string; locationAnchor: string };
  /**
   * The listed ids one run covers, in document order — the anchors whose
   * dependency texts make up the run's fingerprint.
   */
  runListedAnchors: (run: TRun, orderedIds: string[]) => string[];
  /**
   * Resolve one listed id (lazy-loading the document) to its card anchor and
   * preview lines, or an error message when the id is invalid.
   */
  validateAnchor: (anchor: string) => Promise<AnchorValidation>;
};

/**
 * Shared pipeline behind the remove / remove-rows / remove-columns suggestion
 * handlers. Validates every listed id, sorts the ids into document order,
 * short-circuits read-only PDFs and signing-locked documents into a single
 * chat-only `redline-remove`, and otherwise applies each partitioned run as
 * its own suggestion under its own groupId (the sourced edits are the
 * suggestion — no marker pair), emitting one `redline-remove` row per run.
 */
async function applyRemoveSuggestionWithPipeline<TRun>(
  suggest: Pick<RemoveSuggestion, 'documentId' | 'ids' | 'reasoning'>,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode,
  pipeline: RemovePipeline<TRun>
): Promise<CodeBlockRedlineItem[]> {
  const { editorPool, errorReporter, logger, paragraphState } = deps;
  const { documentId, ids, reasoning } = suggest;
  const { nouns } = pipeline;
  const uniqueIds = [...new Set(ids)];

  const removeError = (message: string, locationAnchor: string): CodeBlockRedlineEditError => ({
    documentId,
    groupId: generateSuggestedEditGroupId(),
    locationAnchor,
    message,
    type: 'redline-edit-error',
  });

  /**
   * The model supplies `documentId` as a loose string, so it can arrive empty
   * or as an invented placeholder when no document is attached. An
   * unresolvable target is a per-suggestion problem, not a turn failure, so
   * report it as a `redline-edit-error` and let the rest of the stream
   * proceed.
   */
  if (!documentIdSchema.safeParse(documentId).success) {
    const message = `RedlineAgent emitted an invalid documentId "${documentId}" for ${nouns.applyName} of [${ids.join(', ')}].`;
    logger.warn(message);
    return [removeError(message, uniqueIds[0] ?? '')];
  }

  /**
   * Validate every anchor up front (and lazily load the document's anchor
   * state) so a bad id produces the same rich error reporting as the other
   * suggestion paths. Validation is atomic: one unknown id fails the whole
   * suggestion — partially applying a removal would misrepresent the model's
   * intent.
   */
  const validatedByAnchor = new Map<string, ValidatedAnchor>();
  for (const anchor of uniqueIds) {
    let validation: AnchorValidation;
    try {
      validation = await pipeline.validateAnchor(anchor);
    } catch (error) {
      /**
       * The lazy load inside the validator throws for a valid-looking but
       * unresolvable documentId, such as a deleted document. That fails this
       * suggestion rather than the node. A user Stop must still propagate, so
       * re-throw cancellation before converting anything to an edit error.
       */
      if (getIsCancellationError(error)) throw error;
      const message = `RedlineAgent could not load document "${documentId}" for ${nouns.applyName} anchor "${anchor}": ${error instanceof Error ? error.message : String(error)}`;
      logger.warn(message);
      errorReporter.captureMessage(message, {
        extra: { anchor, documentId, ids },
        level: 'warning',
      });
      return [removeError(message, anchor)];
    }
    if (!validation.ok) {
      logger.error(validation.message);
      const allAnchors = paragraphState.getAllLocationAnchorsForDocument(documentId);
      errorReporter.captureMessage(validation.message, {
        extra: {
          anchor,
          availableAnchorsCount: allAnchors.length,
          availableAnchorsSample: allAnchors.slice(0, 50),
          documentId,
          ids,
        },
        level: 'error',
      });
      return [removeError(validation.message, anchor)];
    }
    validatedByAnchor.set(anchor, validation.value);
  }
  const cardAnchorOf = (anchor: string) => validatedByAnchor.get(anchor)?.cardAnchor ?? anchor;

  /**
   * The removal's dependency fingerprint: the covered anchors' texts,
   * newline-joined in document order. Undefined when any covered anchor has
   * no text dependency (table rows) — those removals carry no fingerprint.
   */
  const textHashOfAnchors = (anchors: string[]): string | undefined => {
    if (anchors.length === 0) return undefined;
    const texts: string[] = [];
    for (const anchor of anchors) {
      const dependencyText = validatedByAnchor.get(anchor)?.dependencyText;
      if (typeof dependencyText !== 'string') return undefined;
      texts.push(dependencyText);
    }
    return SparkMD5.hash(texts.join('\n'));
  };

  /**
   * The schema frames `ids` as an unordered set and the model sometimes emits
   * them bottom-up. Sort them into document order (the paragraph state loads
   * a document's extracted paragraphs in document order) so the card anchors
   * and preview read top-down.
   */
  const orderedIds = sortAnchorsIntoDocumentOrder(uniqueIds, paragraphState, documentId);
  const firstId = orderedIds[0];
  const lastId = orderedIds[orderedIds.length - 1];

  // Missing boundary anchors double as the empty-ids check.
  if (firstId === undefined || lastId === undefined) {
    const message = `RedlineAgent received a ${nouns.suggestionName} suggestion with no ids for document "${documentId}".`;
    logger.error(message);
    errorReporter.captureMessage(message, { extra: { documentId }, level: 'error' });
    return [removeError(message, '')];
  }

  /**
   * Preview mode returns one suggestion covering the listed anchors without
   * applying (run partitioning needs the editor, so the preview card spans
   * the whole set). PDF (Apryse) documents are read-only — their
   * coordinate-based anchors don't exist in the collab editor, so applying
   * would throw — and signing-locked documents (sent for signature or
   * signed) must not receive new edits: both always take this path.
   */
  if (
    mode === 'preview' ||
    getIsPdfLocationAnchor(parseAnchorId(firstId).locationAnchor) ||
    (await editorPool.getSigningLockState(documentId))
  ) {
    return [
      {
        documentId,
        endLocationAnchor: cardAnchorOf(lastId),
        groupId: generateSuggestedEditGroupId(),
        html: orderedIds.map(anchor => validatedByAnchor.get(anchor)?.previewHtml ?? '').join(''),
        locationAnchor: cardAnchorOf(firstId),
        reasoning,
        textHash: textHashOfAnchors(orderedIds),
        type: 'redline-remove',
      } satisfies CodeBlockRedlineRemove,
    ];
  }

  /**
   * Partition the ids into runs (per-type adjacency rules live in the
   * pipeline's partition function), then apply each run as its own
   * suggestion. If a later run's apply throws, the items for the runs already
   * applied are still returned — their marks are in the document, so their
   * cards must exist — followed by one error row.
   */
  const items: CodeBlockRedlineItem[] = [];
  try {
    const editor = await editorPool.getOrCreateEditor(documentId);
    const runs = pipeline.partition(editor, orderedIds);
    for (const run of runs) {
      const groupId = generateSuggestedEditGroupId();
      let removed = { removedHtml: '' };
      AlineTransforms.withoutNormalizing(editor, () => {
        removed = pipeline.applyRun(editor, run, groupId);
      });
      const { endLocationAnchor, locationAnchor } = pipeline.runAnchors(run);
      items.push({
        documentId,
        endLocationAnchor: cardAnchorOf(endLocationAnchor),
        groupId,
        html: removed.removedHtml,
        locationAnchor: cardAnchorOf(locationAnchor),
        reasoning,
        textHash: textHashOfAnchors(pipeline.runListedAnchors(run, orderedIds)),
        type: 'redline-remove',
      } satisfies CodeBlockRedlineRemove);
    }
  } catch (error) {
    const message =
      error instanceof Error
        ? `RedlineAgent ${nouns.applyName} threw for [${orderedIds.join(', ')}]: ${error.message}`
        : `RedlineAgent ${nouns.applyName} threw for [${orderedIds.join(', ')}].`;
    logger.error(message, error);
    errorReporter.captureException(error, { extra: { documentId, ids } });
    return [...items, removeError(message, firstId)];
  }

  return items;
}

/**
 * The validateAnchor for the pipelines whose listed ids are paragraph
 * anchors (remove and remove-columns): the paragraph itself is the card
 * anchor and its text is the preview line.
 */
function createParagraphAnchorValidator(
  paragraphState: ParagraphStateReader,
  documentId: string,
  couldNotFindSuffix: string
) {
  return async (anchor: string): Promise<AnchorValidation> => {
    const paragraphInfo = await paragraphState.getParagraphInfoByLocationAnchorWithLazyLoad(
      documentId,
      anchor
    );
    if (!paragraphInfo) {
      return {
        message: `RedlineAgent could not find paragraph "${anchor}" in document "${documentId}" ${couldNotFindSuffix}.`,
        ok: false,
      };
    }
    return {
      ok: true,
      value: {
        cardAnchor: anchor,
        dependencyText: paragraphInfo.text,
        previewHtml: serializeRemovedTextLinesToPreviewHtml([paragraphInfo.text]),
      },
    };
  };
}

/**
 * Sort location anchors into document order. The paragraph state loads a
 * document's extracted paragraphs in document order, so its per-document
 * anchor list gives each anchor's position. Anchors the state doesn't know
 * (none remain after validation) sort to the end, keeping their relative
 * order.
 */
function sortAnchorsIntoDocumentOrder(
  anchors: string[],
  paragraphState: ParagraphStateReader,
  documentId: string
): string[] {
  const indexByAnchor = new Map(
    paragraphState
      .getAllLocationAnchorsForDocument(documentId)
      .map((anchor, index): [string, number] => [anchor, index])
  );
  const orderOf = (anchor: string) => indexByAnchor.get(anchor) ?? Number.MAX_SAFE_INTEGER;
  return [...anchors].sort((a, b) => orderOf(a) - orderOf(b));
}

/**
 * The card anchors for a run listed as explicit locationAnchors: its first
 * and last paragraphs. The partition functions never produce an empty run,
 * so the throw only exists to narrow the indexed access; if it ever fired it
 * would surface as the suggestion's `redline-edit-error` row.
 */
function getRunBoundaryAnchors(locationAnchors: string[]): {
  endLocationAnchor: string;
  locationAnchor: string;
} {
  const first = locationAnchors[0];
  const last = locationAnchors[locationAnchors.length - 1];
  if (first === undefined || last === undefined) {
    throw new Error('RedlineAgent: partitioned remove run has no location anchors.');
  }
  return { endLocationAnchor: last, locationAnchor: first };
}

/**
 * Apply one streamed `remove` suggestion to the server-side editor and
 * convert it into the `redline-remove` / `redline-edit-error` rows that the
 * client will render. The listed ids are partitioned into contiguous sibling
 * runs (a table between two listed adjacent paragraphs joins the run and is
 * removed with it), one `redline-remove` row per run.
 */
export async function applyRemoveSuggestion(
  suggest: RemoveSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem[]> {
  const { makeSource, paragraphState, userId } = deps;
  return applyRemoveSuggestionWithPipeline(suggest, deps, mode, {
    applyRun: (editor, run, groupId) =>
      applyRemoveOutput(editor, {
        authorId: userId,
        endLocationAnchor: run.endLocationAnchor,
        groupId,
        reasoning: suggest.reasoning,
        source: makeSource(groupId),
        startLocationAnchor: run.startLocationAnchor,
      }),
    nouns: {
      applyName: 'applyRemoveOutput',
      suggestionName: 'remove',
    },
    partition: partitionRemoveAnchorsIntoRuns,
    runAnchors: run => ({
      endLocationAnchor: run.endLocationAnchor,
      locationAnchor: run.startLocationAnchor,
    }),
    /**
     * A remove run is a contiguous slice of the document-ordered listed ids
     * (unlisted blocks swallowed between them, like a table, are not listed
     * and carry no dependency text).
     */
    runListedAnchors: (run, orderedIds) => {
      const startIndex = orderedIds.indexOf(run.startLocationAnchor);
      const endIndex = orderedIds.indexOf(run.endLocationAnchor);
      if (startIndex === -1 || endIndex === -1) return [];
      return orderedIds.slice(startIndex, endIndex + 1);
    },
    validateAnchor: createParagraphAnchorValidator(paragraphState, suggest.documentId, 'to remove'),
  });
}

/**
 * Apply one streamed `remove-rows` suggestion to the server-side editor and
 * convert it into the `redline-remove` / `redline-edit-error` rows that the
 * client will render. The listed `row-…` ids identify the rows directly;
 * adjacent rows (per table) are combined into one suggestion per run.
 */
export async function applyRemoveRowsSuggestion(
  suggest: RemoveRowsSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem[]> {
  const { makeSource, paragraphState, userId } = deps;
  const { documentId } = suggest;

  const validateRowAnchor = async (anchor: string): Promise<AnchorValidation> => {
    const parsed = parseAnchorId(anchor);
    if (parsed.kind !== 'row') {
      return {
        message: `RedlineAgent remove-rows requires "row-…" ids from <tr> elements, got "${anchor}" (a ${parsed.kind} id).`,
        ok: false,
      };
    }
    const blockAnchorInfo = await paragraphState.getBlockAnchorInfoByAnchorIdWithLazyLoad(
      documentId,
      anchor
    );
    if (!blockAnchorInfo) {
      return {
        message: `RedlineAgent could not find table row "${anchor}" in document "${documentId}" to remove.`,
        ok: false,
      };
    }
    return {
      ok: true,
      value: {
        cardAnchor: getBlockCardAnchor(blockAnchorInfo),
        // Row removals have no text dependency — the anchor state carries no
        // row text, and removing a row is intentional regardless of edits
        // inside it.
        dependencyText: null,
        previewHtml: blockAnchorInfo.pdfPreviewHtml ?? '',
      },
    };
  };

  return applyRemoveSuggestionWithPipeline(suggest, deps, mode, {
    applyRun: (editor, run, groupId) =>
      applyRemoveRowsOutput(editor, {
        authorId: userId,
        groupId,
        locationAnchors: run.locationAnchors,
        reasoning: suggest.reasoning,
        source: makeSource(groupId),
      }),
    nouns: {
      applyName: 'applyRemoveRowsOutput',
      suggestionName: 'remove-rows',
    },
    partition: partitionRemoveRowAnchorsIntoRuns,
    runAnchors: run => getRunBoundaryAnchors(run.locationAnchors),
    runListedAnchors: run => run.locationAnchors,
    validateAnchor: validateRowAnchor,
  });
}

/**
 * Apply one streamed `remove-columns` suggestion to the server-side editor
 * and convert it into the `redline-remove` / `redline-edit-error` rows that
 * the client will render. The listed paragraph ids identify table columns;
 * adjacent columns (per table) are combined into one suggestion per run.
 */
export async function applyRemoveColumnsSuggestion(
  suggest: RemoveColumnsSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem[]> {
  const { makeSource, paragraphState, userId } = deps;
  return applyRemoveSuggestionWithPipeline(suggest, deps, mode, {
    applyRun: (editor, run, groupId) =>
      applyRemoveColumnsOutput(editor, {
        authorId: userId,
        groupId,
        locationAnchors: run.locationAnchors,
        reasoning: suggest.reasoning,
        source: makeSource(groupId),
      }),
    nouns: {
      applyName: 'applyRemoveColumnsOutput',
      suggestionName: 'remove-columns',
    },
    partition: partitionRemoveColumnAnchorsIntoRuns,
    runAnchors: run => getRunBoundaryAnchors(run.locationAnchors),
    runListedAnchors: run => run.locationAnchors,
    validateAnchor: createParagraphAnchorValidator(
      paragraphState,
      suggest.documentId,
      'to remove its table column'
    ),
  });
}
